import { useState, useEffect } from 'react';
import { supabase, DISPATCH_MONEY_ROLE_LABELS } from '../../lib/supabase';
import { fetchAllRows } from '../../lib/fetchAllRows';
import { DollarSign, TrendingUp, Calendar, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Search, Edit2, BarChart3, X, Lock } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import { useNavigate } from '../../hooks/useNavigate';

interface DispatchData {
  id: string;
  dispatch_number: string;
  dispatch_date: string;
  status?: string;
  driver_pay: number;
  load_id: string;
  truck_id: string;
  driver_id: string;
  empty_miles?: number;
  loads: {
    load_number: string;
    total_rate: number;
    total_miles: number;
    pickup_date?: string;
    delivery_date?: string;
    load_stops?: Array<{
      stop_type?: string;
      stop_order?: number;
      date?: string;
      time_from?: string;
    }>;
    status?: string;
  };
  trucks: {
    unit_number: string;
    dispatcher_id: string | null;
  };
  drivers: {
    id: string;
    first_name: string;
    last_name: string;
    driver_type?: 'company_driver' | 'owner_operator' | 'rent' | 'buy';
    dispatcher_id?: string | null;
    equipment_assignments?: Array<{
      is_current: boolean;
      trucks: {
        id: string;
        unit_number: string;
        dispatcher_id: string | null;
      };
    }>;
  };
}

interface DriverStats {
  driverId: string;
  driverName: string;
  totalGross: number;
  totalDriverPay: number;
  totalCut: number;
  totalMiles: number;
  dispatchCount: number;
  dispatches: DispatchData[];
}

interface DispatcherStats {
  dispatcherId: string;
  dispatcherName: string;
  totalGross: number;
  totalDriverPay: number;
  totalCut: number;
  totalMiles: number;
  dispatchCount: number;
  drivers: DriverStats[];
}

interface WeekData {
  weekStart: Date;
  weekEnd: Date;
  dispatchers: DispatcherStats[];
}

interface AssignmentTimeline {
  driver_id: string;
  truck_id: string;
  assigned_at: string;
  released_at?: string | null;
  is_current: boolean;
  trucks?: {
    dispatcher_id?: string | null;
  };
}

// Helper function to get the start of the week (Monday)
// A dispatch counts toward a dispatcher's gross and cut unless it was genuinely
// removed. Deliberately does NOT drop status 'archived': that is what
// "Send to Accounting" writes (LoadDetailView.handleSendToAccounting), so it means
// the load was billed, not that it never ran. Excluding it silently erased the
// dispatcher's cut the moment accounting processed the load. The Dispatch Board
// only filters 'removed' (loadDispatches, .neq('status','removed')) and the two
// numbers are read side by side, so they have to agree.
// A missing truck is not a reason to drop a dispatch either: the board shows those
// drivers as "No Truck", and the dispatcher is resolved from loads.created_by
// first, with the truck only a fallback.
const countsTowardGross = (dispatch: any): boolean => {
  if (!dispatch.loads || !dispatch.drivers) return false;
  if (dispatch.status === 'removed') return false;
  const loadNumber = dispatch.loads?.load_number || '';
  if (dispatch.loads?.status === 'removed' || loadNumber.includes('_REMOVED_')) return false;
  return true;
};

const getWeekStart = (date: Date): Date => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

// Helper function to get the end of the week (next Monday)
// Sunday, not next Monday. The weekly breakdown buckets each dispatch by its own
// getWeekStart, so a Monday load belongs to the week that starts that Monday. An
// end of start+7 made the Stats window 8 days and inclusive on both ends, so every
// Monday load was counted in two consecutive weeks — and it made the header label
// ("Sep 21 - Sep 28") claim a day the list below it does not include.
const getWeekEnd = (date: Date): Date => {
  const d = getWeekStart(date);
  d.setDate(d.getDate() + 6);
  d.setHours(23, 59, 59, 999);
  return d;
};

export function DispatchersGrossView() {
  const [weekData, setWeekData] = useState<WeekData | null>(null);
  const [previousWeekData, setPreviousWeekData] = useState<WeekData | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentWeekStart, setCurrentWeekStart] = useState<Date>(() => {
    // Start with current week
    const today = new Date();
    return getWeekStart(today);
  });
  const [expandedDispatchers, setExpandedDispatchers] = useState<Set<string>>(new Set());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState('');
  const [driverSearchQuery, setDriverSearchQuery] = useState('');
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [statsData, setStatsData] = useState<any>(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [expandedStatsSections, setExpandedStatsSections] = useState<Set<string>>(new Set());
  const toast = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    loadDispatchData();
  }, [currentWeekStart]);

  const goToPreviousWeek = () => {
    const newWeekStart = new Date(currentWeekStart);
    newWeekStart.setDate(newWeekStart.getDate() - 7);
    setCurrentWeekStart(newWeekStart);
  };

  const goToNextWeek = () => {
    const newWeekStart = new Date(currentWeekStart);
    newWeekStart.setDate(newWeekStart.getDate() + 7);
    setCurrentWeekStart(newWeekStart);
  };

  const goToCurrentWeek = () => {
    const today = new Date();
    setCurrentWeekStart(getWeekStart(today));
  };

  const toggleDispatcher = (dispatcherId: string) => {
    setExpandedDispatchers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(dispatcherId)) {
        newSet.delete(dispatcherId);
      } else {
        newSet.add(dispatcherId);
      }
      return newSet;
    });
  };

  const handleDateSelection = () => {
    if (selectedDate) {
      const date = new Date(selectedDate);
      setCurrentWeekStart(getWeekStart(date));
      setShowDatePicker(false);
    }
  };

  const formatWeekRange = (start: Date, end: Date): string => {
    const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
    return `${start.toLocaleDateString('en-US', options)} - ${end.toLocaleDateString('en-US', options)}`;
  };

  async function loadStatsData() {
    try {
      setLoadingStats(true);
      
      // Calculate date ranges for last 12 weeks, 12 months, and 3 years
      const now = new Date();
      const threeYearsAgo = new Date(now);
      threeYearsAgo.setFullYear(threeYearsAgo.getFullYear() - 3);
      
      // Fetch all dispatches from the last 3 years
      const dispatches = await fetchAllRows<any>(() => supabase
        .from('dispatches')
        .select(`
          id,
          dispatch_date,
          status,
          driver_pay,
          loads(load_number, total_rate, total_miles, status),
          trucks(dispatcher_id),
          drivers(driver_type)
        `)
        .gte('dispatch_date', threeYearsAgo.toISOString())
        .order('dispatch_date', { ascending: true }));

      // Same predicate the weekly breakdown uses, so Stats and the list agree.
      const validDispatches = (dispatches || []).filter(countsTowardGross);

      // Calculate weekly stats (last 12 weeks)
      const weeklyStats = [];
      for (let i = 11; i >= 0; i--) {
        const weekStart = new Date(now);
        weekStart.setDate(weekStart.getDate() - (i * 7));
        const weekStartDate = getWeekStart(weekStart);
        const weekEndDate = getWeekEnd(weekStartDate);
        
        const weekDispatches = validDispatches.filter((d: any) => {
          const dispatchDate = new Date(d.dispatch_date);
          return dispatchDate >= weekStartDate && dispatchDate <= weekEndDate;
        });
        
        const totalGross = weekDispatches.reduce((sum: number, d: any) => sum + (d.loads?.total_rate || 0), 0);
        const totalMiles = weekDispatches.reduce((sum: number, d: any) => sum + (d.loads?.total_miles || 0), 0);
        const totalCut = weekDispatches.reduce((sum: number, d: any) => {
          const gross = d.loads?.total_rate || 0;
          const driverPay = d.driver_pay || 0;
          return sum + (gross - driverPay);
        }, 0);
        
        weeklyStats.push({
          period: formatWeekRange(weekStartDate, weekEndDate),
          totalGross,
          totalMiles,
          avgPerMile: totalMiles > 0 ? totalGross / totalMiles : 0,
          totalCut
        });
      }

      // Calculate monthly stats (last 12 months)
      const monthlyStats = [];
      for (let i = 11; i >= 0; i--) {
        const monthDate = new Date(now);
        monthDate.setMonth(monthDate.getMonth() - i);
        const monthStart = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
        const monthEnd = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0, 23, 59, 59, 999);
        
        const monthDispatches = validDispatches.filter((d: any) => {
          const dispatchDate = new Date(d.dispatch_date);
          return dispatchDate >= monthStart && dispatchDate <= monthEnd;
        });
        
        const totalGross = monthDispatches.reduce((sum: number, d: any) => sum + (d.loads?.total_rate || 0), 0);
        const totalMiles = monthDispatches.reduce((sum: number, d: any) => sum + (d.loads?.total_miles || 0), 0);
        const totalCut = monthDispatches.reduce((sum: number, d: any) => {
          const gross = d.loads?.total_rate || 0;
          const driverPay = d.driver_pay || 0;
          return sum + (gross - driverPay);
        }, 0);
        
        monthlyStats.push({
          period: monthStart.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
          totalGross,
          totalMiles,
          avgPerMile: totalMiles > 0 ? totalGross / totalMiles : 0,
          totalCut
        });
      }

      // Calculate yearly stats (only current year 2026)
      const yearlyStats = [];
      const currentYear = 2026;
      for (let i = 0; i <= 0; i++) {
        const year = currentYear - i;
        const yearStart = new Date(year, 0, 1);
        const yearEnd = new Date(year, 11, 31, 23, 59, 59, 999);
        
        const yearDispatches = validDispatches.filter((d: any) => {
          const dispatchDate = new Date(d.dispatch_date);
          return dispatchDate >= yearStart && dispatchDate <= yearEnd;
        });
        
        const totalGross = yearDispatches.reduce((sum: number, d: any) => sum + (d.loads?.total_rate || 0), 0);
        const totalMiles = yearDispatches.reduce((sum: number, d: any) => sum + (d.loads?.total_miles || 0), 0);
        const totalCut = yearDispatches.reduce((sum: number, d: any) => {
          const gross = d.loads?.total_rate || 0;
          const driverPay = d.driver_pay || 0;
          return sum + (gross - driverPay);
        }, 0);
        
        yearlyStats.push({
          period: year.toString(),
          totalGross,
          totalMiles,
          avgPerMile: totalMiles > 0 ? totalGross / totalMiles : 0,
          totalCut
        });
      }

      // Filter out weeks and months with no data
      const filteredWeeklyStats = weeklyStats.filter((stat: any) => stat.totalGross > 0 || stat.totalMiles > 0);
      const filteredMonthlyStats = monthlyStats.filter((stat: any) => stat.totalGross > 0 || stat.totalMiles > 0);
      
      setStatsData({
        weekly: filteredWeeklyStats,
        monthly: filteredMonthlyStats,
        yearly: yearlyStats
      });
    } catch (error) {
      console.error('Error loading stats data:', error);
      toast.error('Failed to load statistics');
    } finally {
      setLoadingStats(false);
    }
  }

  async function loadDispatchData() {
    try {
      setLoading(true);

      // Calculate date range for current week
      const weekEnd = getWeekEnd(currentWeekStart);
      const startDate = new Date(currentWeekStart);
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(weekEnd);
      endDate.setHours(23, 59, 59, 999);

      // Calculate previous week range
      const prevWeekStart = new Date(currentWeekStart);
      prevWeekStart.setDate(prevWeekStart.getDate() - 7);
      const prevWeekEnd = getWeekEnd(prevWeekStart);
      const prevStartDate = new Date(prevWeekStart);
      prevStartDate.setHours(0, 0, 0, 0);
      const prevEndDate = new Date(prevWeekEnd);
      prevEndDate.setHours(23, 59, 59, 999);

      // Fetch dispatches - include loads.created_by to know who booked each load
      const dispatchesData = await fetchAllRows<any>(() => supabase
        .from('dispatches')
        .select(`
          id,
          dispatch_number,
          dispatch_date,
          status,
          driver_pay,
          load_id,
          truck_id,
          driver_id,
          empty_miles,
          loads(load_number, total_rate, total_miles, pickup_date, delivery_date, status, created_by, load_stops(stop_type, stop_order, date, time_from)),
          trucks(id, unit_number, dispatcher_id),
          drivers(id, first_name, last_name, driver_type, equipment_assignments(is_current, trucks(id, unit_number, dispatcher_id)))
        `)
        .order('dispatch_date', { ascending: false }));
      const driverIds = Array.from(new Set(dispatchesData.map(d => d.driver_id).filter(Boolean)));

      // Fetch assignment timeline to resolve historical dispatcher at dispatch time
      const { data: assignmentTimeline, error: assignmentError } = await supabase
        .from('equipment_assignments')
        .select('driver_id, truck_id, assigned_at, released_at, is_current, trucks(dispatcher_id)')
        .in('driver_id', driverIds)
        .order('assigned_at', { ascending: true });

      if (assignmentError) {
        console.error('Error fetching assignment timeline:', assignmentError);
      }

      const timelineByDriver = new Map<string, AssignmentTimeline[]>();
      (assignmentTimeline as AssignmentTimeline[] || []).forEach((entry) => {
        if (!entry.driver_id) return;
        const list = timelineByDriver.get(entry.driver_id) || [];
        list.push(entry);
        timelineByDriver.set(entry.driver_id, list);
      });

      const getDispatchTimestamp = (dispatch: any): Date => {
        const stops = dispatch.loads?.load_stops || [];
        const sortedStops = [...stops].sort((a: any, b: any) => (a.stop_order || 0) - (b.stop_order || 0));
        const pickupStop = sortedStops.find((s: any) => s.stop_type === 'shipper') || sortedStops[0];
        const pickupDateTime = pickupStop?.date && pickupStop?.time_from
          ? `${pickupStop.date.split('T')[0]}T${pickupStop.time_from}`
          : pickupStop?.date || dispatch.loads?.pickup_date || dispatch.dispatch_date;
        return new Date(pickupDateTime);
      };

      const resolveHistoricalDispatcherId = (dispatch: any): string => {
        // 1. Best source: who created/booked the load (the actual dispatcher)
        if (dispatch.loads?.created_by) {
          return dispatch.loads.created_by;
        }

        // 2. Fallback: resolve from equipment_assignments timeline
        const dispatchTs = getDispatchTimestamp(dispatch).getTime();
        const driverTimeline = timelineByDriver.get(dispatch.driver_id) || [];

        const activeAtTime = driverTimeline.filter((entry) => {
          const assignedTs = new Date(entry.assigned_at).getTime();
          const releasedTs = entry.released_at ? new Date(entry.released_at).getTime() : null;
          return assignedTs <= dispatchTs && (releasedTs === null || dispatchTs < releasedTs);
        });

        const truckMatched = activeAtTime.find((entry) => entry.truck_id === dispatch.truck_id);
        const selected = truckMatched || activeAtTime[0];

        // 4. Last fallback: current truck dispatcher
        return selected?.trucks?.dispatcher_id
          || dispatch.trucks?.dispatcher_id
          || 'unassigned';
      };

      // Collect all unique dispatcher IDs from dispatches
      // Use same logic as Dispatch Board: get dispatcher from truck via equipment_assignments
      const dispatcherIds = new Set<string>();
      dispatchesData.forEach((dispatch: any) => {
        const dispatcherId = resolveHistoricalDispatcherId(dispatch);
        if (dispatcherId) {
          dispatcherIds.add(dispatcherId);
        }
      });

      // Filter out 'unassigned' - it's not a valid UUID and would cause a 400 error
      const dispatcherIdsArray = Array.from(dispatcherIds).filter(id => id !== 'unassigned');

      // Fetch dispatcher names from profiles table for the specific dispatcher IDs
      const { data: profiles, error: profilesError } = dispatcherIdsArray.length > 0
        ? await supabase
            .from('profiles')
            .select('id, full_name')
            .in('id', dispatcherIdsArray)
        : { data: [], error: null };

      if (profilesError) {
        console.error('Error fetching profiles:', profilesError);
      }

      const userMap = new Map(profiles?.map(u => [u.id, u.full_name]) || []);

      // Group by week
      const weekMap = new Map<string, DispatchData[]>();

      dispatchesData.forEach((dispatch: any) => {
        if (!countsTowardGross(dispatch)) {
          return;
        }
        
        const dispatchDate = getDispatchTimestamp(dispatch);
        const weekStart = getWeekStart(dispatchDate);
        const weekKey = weekStart.toISOString();

        if (!weekMap.has(weekKey)) {
          weekMap.set(weekKey, []);
        }
        weekMap.get(weekKey)!.push(dispatch);
      });
      
      // Process current week
      const weekKey = currentWeekStart.toISOString();
      const weekDispatches = weekMap.get(weekKey) || [];
      
      // Process previous week
      const prevWeekKey = prevWeekStart.toISOString();
      const prevWeekDispatches = weekMap.get(prevWeekKey) || [];

      // Group by dispatcher
      const dispatcherMap = new Map<string, DispatcherStats>();

      weekDispatches.forEach(dispatch => {
        const dispatcherId = resolveHistoricalDispatcherId(dispatch);
        const dispatcherName = dispatcherId === 'unassigned' ? 'Unassigned' : (userMap.get(dispatcherId) || 'Unknown Dispatcher');
        const driverId = dispatch.driver_id;
        const driverName = `${dispatch.drivers.first_name} ${dispatch.drivers.last_name}`;

        // Initialize dispatcher if not exists
        if (!dispatcherMap.has(dispatcherId)) {
          dispatcherMap.set(dispatcherId, {
            dispatcherId,
            dispatcherName,
            totalGross: 0,
            totalDriverPay: 0,
            totalCut: 0,
            totalMiles: 0,
            dispatchCount: 0,
            drivers: []
          });
        }

        const dispatcherStats = dispatcherMap.get(dispatcherId)!;
        
        // Find or create driver within this dispatcher
        let driverStats = dispatcherStats.drivers.find(d => d.driverId === driverId);
        if (!driverStats) {
          driverStats = {
            driverId,
            driverName,
            totalGross: 0,
            totalDriverPay: 0,
            totalCut: 0,
            totalMiles: 0,
            dispatchCount: 0,
            dispatches: []
          };
          dispatcherStats.drivers.push(driverStats);
        }

        const gross = dispatch.loads.total_rate || 0;
        const driverPay = dispatch.driver_pay || 0;
        const loadedMiles = dispatch.loads.total_miles || 0;
        const emptyMiles = dispatch.empty_miles || 0;
        const miles = loadedMiles + emptyMiles; // Total miles = loaded + empty
        const cut = gross - driverPay;

        // Update dispatcher totals
        dispatcherStats.totalGross += gross;
        dispatcherStats.totalDriverPay += driverPay;
        dispatcherStats.totalCut += cut;
        dispatcherStats.totalMiles += miles;
        dispatcherStats.dispatchCount++;

        // Update driver totals
        driverStats.totalGross += gross;
        driverStats.totalDriverPay += driverPay;
        driverStats.totalCut += cut;
        driverStats.totalMiles += miles;
        driverStats.dispatchCount++;
        driverStats.dispatches.push(dispatch);
      });

      // Sort dispatchers by total gross
      const dispatchersArray = Array.from(dispatcherMap.values()).sort((a, b) => 
        b.totalGross - a.totalGross
      );
      
      // Sort drivers within each dispatcher
      dispatchersArray.forEach(dispatcher => {
        dispatcher.drivers.sort((a, b) => b.totalGross - a.totalGross);
      });

      // Process previous week data
      const prevDispatcherMap = new Map<string, DispatcherStats>();
      
      prevWeekDispatches.forEach(dispatch => {
        const dispatcherId = resolveHistoricalDispatcherId(dispatch);
        const dispatcherName = dispatcherId === 'unassigned' ? 'Unassigned' : (userMap.get(dispatcherId) || 'Unknown Dispatcher');
        const driverId = dispatch.driver_id;
        const driverName = `${dispatch.drivers.first_name} ${dispatch.drivers.last_name}`;

        if (!prevDispatcherMap.has(dispatcherId)) {
          prevDispatcherMap.set(dispatcherId, {
            dispatcherId,
            dispatcherName,
            totalGross: 0,
            totalDriverPay: 0,
            totalCut: 0,
            totalMiles: 0,
            dispatchCount: 0,
            drivers: []
          });
        }

        const dispatcherStats = prevDispatcherMap.get(dispatcherId)!;
        
        let driverStats = dispatcherStats.drivers.find(d => d.driverId === driverId);
        if (!driverStats) {
          driverStats = {
            driverId,
            driverName,
            totalGross: 0,
            totalDriverPay: 0,
            totalCut: 0,
            totalMiles: 0,
            dispatchCount: 0,
            dispatches: []
          };
          dispatcherStats.drivers.push(driverStats);
        }

        const gross = dispatch.loads.total_rate || 0;
        const driverPay = dispatch.driver_pay || 0;
        const loadedMiles = dispatch.loads.total_miles || 0;
        const emptyMiles = dispatch.empty_miles || 0;
        const miles = loadedMiles + emptyMiles; // Total miles = loaded + empty
        const cut = gross - driverPay;

        dispatcherStats.totalGross += gross;
        dispatcherStats.totalDriverPay += driverPay;
        dispatcherStats.totalCut += cut;
        dispatcherStats.totalMiles += miles;
        dispatcherStats.dispatchCount++;

        driverStats.totalGross += gross;
        driverStats.totalDriverPay += driverPay;
        driverStats.totalCut += cut;
        driverStats.totalMiles += miles;
        driverStats.dispatchCount++;
        driverStats.dispatches.push(dispatch);
      });

      const prevDispatchersArray = Array.from(prevDispatcherMap.values());
      
      setWeekData({
        weekStart: currentWeekStart,
        weekEnd: weekEnd,
        dispatchers: dispatchersArray
      });
      
      setPreviousWeekData({
        weekStart: prevWeekStart,
        weekEnd: prevWeekEnd,
        dispatchers: prevDispatchersArray
      });
    } catch (error) {
      console.error('Error loading dispatch data:', error);
      toast.error('Failed to load dispatcher data');
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-600">Loading dispatcher data...</div>
      </div>
    );
  }

  const normalizedDriverSearch = driverSearchQuery.trim().toLowerCase();
  const filteredDispatchers = weekData?.dispatchers
    ? (normalizedDriverSearch
      ? weekData.dispatchers.filter((dispatcher) =>
          dispatcher.drivers.some((driver) =>
            driver.driverName.toLowerCase().includes(normalizedDriverSearch)
          )
        )
      : weekData.dispatchers)
    : [];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <DollarSign className="w-7 h-7 text-green-600" />
              Driver Gross Performance
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              Track driver earnings and performance per week
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={goToPreviousWeek}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              title="Previous week"
            >
              <ChevronLeft className="w-5 h-5 text-slate-600" />
            </button>
            <div className="flex items-center gap-2 bg-blue-50 px-4 py-2 rounded-lg border border-blue-200">
              <Calendar className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-semibold text-slate-900">
                {weekData ? formatWeekRange(weekData.weekStart, weekData.weekEnd) : 'Loading...'}
              </span>
            </div>
            <button
              onClick={goToNextWeek}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              title="Next week"
            >
              <ChevronRight className="w-5 h-5 text-slate-600" />
            </button>
            <button
              onClick={goToCurrentWeek}
              className="px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Current Week
            </button>
            <button
              onClick={() => setShowDatePicker(!showDatePicker)}
              className="px-3 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2"
              title="Jump to specific week"
            >
              <Calendar className="w-4 h-4" />
              Jump to Date
            </button>
            <button
              onClick={() => {
                setShowStatsModal(true);
                loadStatsData();
              }}
              className="px-3 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors flex items-center gap-2"
              title="View statistics"
            >
              <BarChart3 className="w-4 h-4" />
              Stats
            </button>
          </div>
        </div>

        {/* Who can see what. Only roles that can open this page ever see this notice. */}
        <div className="mt-4 flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
          <Lock className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
          <div className="text-sm text-slate-700 space-y-1">
            <p className="font-semibold text-slate-900">Who can see these numbers</p>
            <p>
              <span className="font-medium">This page:</span> only {DISPATCH_MONEY_ROLE_LABELS.join(', ')}.
            </p>
            <p>
              <span className="font-medium">Dispatch Board:</span> load rates, driver pay, gross and RPM are shown only to these same roles.
              Everyone else (Dispatch, Dispatch Manager, Safety, Safety Manager, Maintenance, ELD, Afterhours) sees the board without any dollar amounts.
            </p>
          </div>
        </div>
      </div>

      {/* Date Picker Modal */}
      {showDatePicker && (
        <div className="mb-6 bg-white rounded-lg border border-purple-200 p-4 shadow-lg">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-slate-900">Jump to Specific Week</h3>
            <button
              onClick={() => setShowDatePicker(false)}
              className="text-slate-400 hover:text-slate-600"
            >
              ✕
            </button>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <label className="block text-xs text-slate-600 mb-1">Select any date in the week</label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
            </div>
            <button
              onClick={handleDateSelection}
              disabled={!selectedDate}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Go to Week
            </button>
          </div>
          <p className="text-xs text-slate-500 mt-2">
            The system will automatically show the full week (Sunday-Saturday) containing the selected date.
          </p>
        </div>
      )}

      {weekData && weekData.dispatchers.length > 0 && (
        <div className="mb-6">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={driverSearchQuery}
              onChange={(e) => setDriverSearchQuery(e.target.value)}
              placeholder="Search driver name..."
              className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      )}

      {!weekData || weekData.dispatchers.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-slate-200">
          <DollarSign className="w-12 h-12 text-slate-400 mx-auto mb-3" />
          <p className="text-slate-600">No dispatch data found for this week</p>
        </div>
      ) : filteredDispatchers.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-slate-200">
          <Search className="w-10 h-10 text-slate-400 mx-auto mb-3" />
          <p className="text-slate-600">No drivers found for "{driverSearchQuery}"</p>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            {/* Week Header */}
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 px-6 py-4 border-b border-blue-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-blue-600" />
                  <h2 className="text-lg font-bold text-slate-900">
                    {formatWeekRange(weekData.weekStart, weekData.weekEnd)}
                  </h2>
                </div>
                <div>
                  {(() => {
                    const currentGross = weekData.dispatchers.reduce((sum: number, d: DispatcherStats) => sum + d.totalGross, 0);
                    const currentMiles = weekData.dispatchers.reduce((sum: number, d: DispatcherStats) => sum + d.totalMiles, 0);
                    const currentCut = weekData.dispatchers.reduce((sum: number, d: DispatcherStats) => sum + d.totalCut, 0);
                    const currentRpm = currentMiles > 0 ? currentGross / currentMiles : 0;
                    
                    const prevGross = previousWeekData?.dispatchers.reduce((sum: number, d: DispatcherStats) => sum + d.totalGross, 0) || 0;
                    const prevMiles = previousWeekData?.dispatchers.reduce((sum: number, d: DispatcherStats) => sum + d.totalMiles, 0) || 0;
                    const prevCut = previousWeekData?.dispatchers.reduce((sum: number, d: DispatcherStats) => sum + d.totalCut, 0) || 0;
                    const prevRpm = prevMiles > 0 ? prevGross / prevMiles : 0;
                    
                    const grossChange = prevGross > 0 ? ((currentGross - prevGross) / prevGross) * 100 : 0;
                    const milesChange = prevMiles > 0 ? ((currentMiles - prevMiles) / prevMiles) * 100 : 0;
                    const rpmChange = prevRpm > 0 ? ((currentRpm - prevRpm) / prevRpm) * 100 : 0;
                    const cutChange = prevCut > 0 ? ((currentCut - prevCut) / prevCut) * 100 : 0;
                    
                    return (
                      <>
                        {/* Main metrics row */}
                        <div className="flex items-start gap-6">
                          <div className="text-right min-w-[140px]">
                            <p className="text-xs text-slate-600">Total Gross</p>
                            <p className="text-lg font-bold text-green-600">
                              ${currentGross.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </p>
                            {previousWeekData && (
                              <p className={`text-[10px] font-semibold mt-1 ${grossChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {grossChange >= 0 ? '↑' : '↓'} {Math.abs(grossChange).toFixed(1)}% vs last week
                              </p>
                            )}
                          </div>
                          <div className="text-right min-w-[100px]">
                            <p className="text-xs text-slate-600">Total Miles</p>
                            <p className="text-lg font-bold text-blue-600">
                              {currentMiles.toLocaleString()}
                            </p>
                            {previousWeekData && (
                              <p className={`text-[10px] font-semibold mt-1 ${milesChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {milesChange >= 0 ? '↑' : '↓'} {Math.abs(milesChange).toFixed(1)}% vs last week
                              </p>
                            )}
                          </div>
                          <div className="text-right min-w-[120px]">
                            <p className="text-xs text-slate-600">Avg per Mile</p>
                            <p className="text-lg font-bold text-purple-600">
                              ${currentRpm.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </p>
                            {previousWeekData && (
                              <p className={`text-[10px] font-semibold mt-1 ${rpmChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {rpmChange >= 0 ? '↑' : '↓'} {Math.abs(rpmChange).toFixed(1)}% vs last week
                              </p>
                            )}
                          </div>
                          <div className="text-right min-w-[120px]">
                            <p className="text-xs text-slate-600">Total Cut</p>
                            <p className="text-lg font-bold text-orange-600">
                              ${currentCut.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </p>
                            {previousWeekData && (
                              <p className={`text-[10px] font-semibold mt-1 ${cutChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                {cutChange >= 0 ? '↑' : '↓'} {Math.abs(cutChange).toFixed(1)}% vs last week
                              </p>
                            )}
                          </div>
                        </div>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>

            {/* Dispatchers */}
            <div className="divide-y divide-slate-200">
              {filteredDispatchers.map((dispatcher: DispatcherStats) => {
                const avgPerMile = dispatcher.totalMiles > 0 ? dispatcher.totalGross / dispatcher.totalMiles : 0;
                const isExpanded = expandedDispatchers.has(dispatcher.dispatcherId);
                const filteredDrivers = normalizedDriverSearch
                  ? dispatcher.drivers.filter((driver) =>
                      driver.driverName.toLowerCase().includes(normalizedDriverSearch)
                    )
                  : dispatcher.drivers;

                return (
                  <div key={dispatcher.dispatcherId} className="hover:bg-slate-50 transition-colors">
                    <div 
                      className="px-6 py-4 cursor-pointer"
                      onClick={() => toggleDispatcher(dispatcher.dispatcherId)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <button className="p-1 hover:bg-slate-200 rounded transition-colors">
                            {isExpanded ? (
                              <ChevronUp className="w-4 h-4 text-slate-600" />
                            ) : (
                              <ChevronDown className="w-4 h-4 text-slate-600" />
                            )}
                          </button>
                          <TrendingUp className="w-5 h-5 text-purple-600" />
                          <div>
                            <h3 className="font-semibold text-slate-900">{dispatcher.dispatcherName}</h3>
                            <p className="text-xs text-slate-600">
                              {dispatcher.dispatchCount} dispatch{dispatcher.dispatchCount !== 1 ? 'es' : ''} • {dispatcher.totalMiles.toLocaleString()} miles • {dispatcher.drivers.length} driver{dispatcher.drivers.length !== 1 ? 's' : ''}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-6">
                          <div className="text-right min-w-[140px]">
                            <p className="text-xs text-slate-600">Total Gross</p>
                            <p className="text-base font-bold text-green-600">
                              ${dispatcher.totalGross.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </p>
                          </div>
                          <div className="text-right min-w-[100px]">
                            <p className="text-xs text-slate-600">Total Miles</p>
                            <p className="text-base font-bold text-indigo-600">
                              {dispatcher.totalMiles.toLocaleString()}
                            </p>
                          </div>
                          <div className="text-right min-w-[120px]">
                            <p className="text-xs text-slate-600">Avg per Mile</p>
                            <p className="text-base font-bold text-blue-600">
                              ${avgPerMile.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </p>
                          </div>
                          <div className="text-right min-w-[120px]">
                            <p className="text-xs text-slate-600">Total Cut</p>
                            <p className="text-base font-semibold text-orange-600">
                              ${dispatcher.totalCut.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Expanded Details - Show Drivers and Loads */}
                    {isExpanded && (
                      <div className="px-6 py-4 bg-slate-50 border-t border-slate-200">
                        <h4 className="text-sm font-semibold text-slate-700 mb-3">Drivers & Loads</h4>
                        <div className="space-y-3">
                          {filteredDrivers.map((driver: DriverStats) => (
                            <div key={driver.driverId} className="bg-white rounded-lg p-4 border border-slate-200">
                              {(() => {
                                const driverTotalRpm = driver.totalMiles > 0 ? driver.totalGross / driver.totalMiles : 0;
                                return (
                              <div className="flex items-center justify-between mb-2">
                                <div>
                                  <h5 className="font-semibold text-slate-900">{driver.driverName}</h5>
                                  <p className="text-xs text-slate-600">
                                    {driver.dispatchCount} dispatch{driver.dispatchCount !== 1 ? 'es' : ''} • {driver.totalMiles.toLocaleString()} miles
                                  </p>
                                </div>
                                <div className="flex items-center gap-4 text-sm">
                                  <div className="text-right">
                                    <p className="text-xs text-slate-600">Miles</p>
                                    <p className="font-semibold text-indigo-600">{driver.totalMiles.toLocaleString()}</p>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-xs text-slate-600">RPM</p>
                                    <p className="font-semibold text-blue-600">${driverTotalRpm.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                                  </div>
                                  <div className="text-right">
                                    <p className="text-xs text-slate-600">Gross</p>
                                    <p className="font-bold text-green-600">${driver.totalGross.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                                  </div>
                                  <div className="text-right ml-6">
                                    <p className="text-xs text-slate-600">Cut</p>
                                    <p className="font-semibold text-orange-600">${driver.totalCut.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                                  </div>
                                </div>
                              </div>
                                );
                              })()}
                              {/* Show individual loads */}
                              <div className="mt-2 space-y-1">
                                {driver.dispatches.map((dispatch: DispatchData) => {
                                  const gross = dispatch.loads.total_rate || 0;
                                  const driverPay = dispatch.driver_pay || 0;
                                  const loadedMiles = dispatch.loads.total_miles || 0;
                                  const emptyMiles = dispatch.empty_miles || 0;
                                  const totalMiles = loadedMiles + emptyMiles;
                                  const rpm = loadedMiles > 0 ? gross / loadedMiles : 0;
                                  const loadDriverName = `${dispatch.drivers?.first_name || ''} ${dispatch.drivers?.last_name || ''}`.trim() || driver.driverName;
                                  const loadCut = gross - driverPay;

                                  return (
                                    <div 
                                      key={dispatch.id} 
                                      className="text-xs text-slate-600 flex items-center justify-between py-1 px-2 bg-slate-50 rounded hover:bg-blue-50 hover:border-blue-300 border border-transparent transition-colors cursor-pointer group"
                                      onClick={() => navigate(`edit-load?loadNumber=${dispatch.loads.load_number}`)}
                                      title="Click to edit load and driver pay"
                                    >
                                      <div className="flex items-center gap-2">
                                        <Edit2 className="w-3 h-3 text-slate-400 group-hover:text-blue-600 transition-colors" />
                                        <span>Load #{dispatch.loads.load_number}</span>
                                        <span className="text-slate-500">• Driver: {loadDriverName}</span>
                                      </div>
                                      <div className="flex items-center gap-3">
                                        <span>{loadedMiles.toLocaleString()} mi</span>
                                        {emptyMiles > 0 && <span className="text-amber-700">Empty: {emptyMiles.toLocaleString()} mi</span>}
                                        {emptyMiles > 0 && <span className="text-slate-500">Total: {totalMiles.toLocaleString()} mi</span>}
                                        <span className="text-blue-600">RPM: ${rpm.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                        <span className="font-semibold text-green-600">${gross.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                                        <span className="text-purple-600">Driver Pay: ${driverPay.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                                        <span className="text-orange-600">Cut: ${loadCut.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Stats Modal */}
      {showStatsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <BarChart3 className="w-6 h-6 text-emerald-600" />
                <h2 className="text-xl font-bold text-slate-900">Performance Statistics</h2>
              </div>
              <button
                onClick={() => setShowStatsModal(false)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-slate-600" />
              </button>
            </div>

            {loadingStats ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-slate-600">Loading statistics...</div>
              </div>
            ) : statsData ? (
              <div className="p-6 space-y-8">
                {/* Weekly Stats */}
                <div>
                  <button
                    onClick={() => {
                      setExpandedStatsSections(prev => {
                        const newSet = new Set(prev);
                        if (newSet.has('weekly')) {
                          newSet.delete('weekly');
                        } else {
                          newSet.add('weekly');
                        }
                        return newSet;
                      });
                    }}
                    className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-blue-600" />
                      <h3 className="text-lg font-bold text-slate-900">
                        Weekly Performance (Last 12 Weeks)
                      </h3>
                    </div>
                    {expandedStatsSections.has('weekly') ? (
                      <ChevronUp className="w-5 h-5 text-slate-600" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-slate-600" />
                    )}
                  </button>
                  {expandedStatsSections.has('weekly') && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100 border-b border-slate-200">
                        <tr>
                          <th className="px-4 py-3 text-left font-semibold text-slate-700">Week</th>
                          <th className="px-4 py-3 text-right font-semibold text-slate-700">Total Gross</th>
                          <th className="px-4 py-3 text-right font-semibold text-slate-700">Total Miles</th>
                          <th className="px-4 py-3 text-right font-semibold text-slate-700">Avg per Mile</th>
                          <th className="px-4 py-3 text-right font-semibold text-slate-700">Total Cut</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        {statsData.weekly.map((stat: any, idx: number) => {
                          const prevStat = idx > 0 ? statsData.weekly[idx - 1] : null;
                          const grossChange = prevStat && prevStat.totalGross > 0 ? ((stat.totalGross - prevStat.totalGross) / prevStat.totalGross) * 100 : 0;
                          const milesChange = prevStat && prevStat.totalMiles > 0 ? ((stat.totalMiles - prevStat.totalMiles) / prevStat.totalMiles) * 100 : 0;
                          const rpmChange = prevStat && prevStat.avgPerMile > 0 ? ((stat.avgPerMile - prevStat.avgPerMile) / prevStat.avgPerMile) * 100 : 0;
                          const cutChange = prevStat && prevStat.totalCut > 0 ? ((stat.totalCut - prevStat.totalCut) / prevStat.totalCut) * 100 : 0;
                          
                          return (
                            <tr key={idx} className="hover:bg-slate-50">
                              <td className="px-4 py-3 font-medium text-slate-900">{stat.period}</td>
                              <td className="px-4 py-3 text-right">
                                <div className="font-semibold text-green-600">${stat.totalGross.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
                                {prevStat && <div className={`text-[10px] font-semibold ${grossChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>{grossChange >= 0 ? '↑' : '↓'} {Math.abs(grossChange).toFixed(1)}%</div>}
                              </td>
                              <td className="px-4 py-3 text-right">
                                <div className="font-semibold text-blue-600">{stat.totalMiles.toLocaleString()}</div>
                                {prevStat && <div className={`text-[10px] font-semibold ${milesChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>{milesChange >= 0 ? '↑' : '↓'} {Math.abs(milesChange).toFixed(1)}%</div>}
                              </td>
                              <td className="px-4 py-3 text-right">
                                <div className="font-semibold text-purple-600">${stat.avgPerMile.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
                                {prevStat && <div className={`text-[10px] font-semibold ${rpmChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>{rpmChange >= 0 ? '↑' : '↓'} {Math.abs(rpmChange).toFixed(1)}%</div>}
                              </td>
                              <td className="px-4 py-3 text-right">
                                <div className="font-semibold text-orange-600">${stat.totalCut.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
                                {prevStat && <div className={`text-[10px] font-semibold ${cutChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>{cutChange >= 0 ? '↑' : '↓'} {Math.abs(cutChange).toFixed(1)}%</div>}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  )}
                </div>

                {/* Monthly Stats */}
                <div>
                  <button
                    onClick={() => {
                      setExpandedStatsSections(prev => {
                        const newSet = new Set(prev);
                        if (newSet.has('monthly')) {
                          newSet.delete('monthly');
                        } else {
                          newSet.add('monthly');
                        }
                        return newSet;
                      });
                    }}
                    className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-purple-600" />
                      <h3 className="text-lg font-bold text-slate-900">
                        Monthly Performance (Last 12 Months)
                      </h3>
                    </div>
                    {expandedStatsSections.has('monthly') ? (
                      <ChevronUp className="w-5 h-5 text-slate-600" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-slate-600" />
                    )}
                  </button>
                  {expandedStatsSections.has('monthly') && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100 border-b border-slate-200">
                        <tr>
                          <th className="px-4 py-3 text-left font-semibold text-slate-700">Month</th>
                          <th className="px-4 py-3 text-right font-semibold text-slate-700">Total Gross</th>
                          <th className="px-4 py-3 text-right font-semibold text-slate-700">Total Miles</th>
                          <th className="px-4 py-3 text-right font-semibold text-slate-700">Avg per Mile</th>
                          <th className="px-4 py-3 text-right font-semibold text-slate-700">Total Cut</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        {statsData.monthly.map((stat: any, idx: number) => {
                          const prevStat = idx > 0 ? statsData.monthly[idx - 1] : null;
                          const grossChange = prevStat && prevStat.totalGross > 0 ? ((stat.totalGross - prevStat.totalGross) / prevStat.totalGross) * 100 : 0;
                          const milesChange = prevStat && prevStat.totalMiles > 0 ? ((stat.totalMiles - prevStat.totalMiles) / prevStat.totalMiles) * 100 : 0;
                          const rpmChange = prevStat && prevStat.avgPerMile > 0 ? ((stat.avgPerMile - prevStat.avgPerMile) / prevStat.avgPerMile) * 100 : 0;
                          const cutChange = prevStat && prevStat.totalCut > 0 ? ((stat.totalCut - prevStat.totalCut) / prevStat.totalCut) * 100 : 0;
                          
                          return (
                            <tr key={idx} className="hover:bg-slate-50">
                              <td className="px-4 py-3 font-medium text-slate-900">{stat.period}</td>
                              <td className="px-4 py-3 text-right">
                                <div className="font-semibold text-green-600">${stat.totalGross.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
                                {prevStat && <div className={`text-[10px] font-semibold ${grossChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>{grossChange >= 0 ? '↑' : '↓'} {Math.abs(grossChange).toFixed(1)}%</div>}
                              </td>
                              <td className="px-4 py-3 text-right">
                                <div className="font-semibold text-blue-600">{stat.totalMiles.toLocaleString()}</div>
                                {prevStat && <div className={`text-[10px] font-semibold ${milesChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>{milesChange >= 0 ? '↑' : '↓'} {Math.abs(milesChange).toFixed(1)}%</div>}
                              </td>
                              <td className="px-4 py-3 text-right">
                                <div className="font-semibold text-purple-600">${stat.avgPerMile.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
                                {prevStat && <div className={`text-[10px] font-semibold ${rpmChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>{rpmChange >= 0 ? '↑' : '↓'} {Math.abs(rpmChange).toFixed(1)}%</div>}
                              </td>
                              <td className="px-4 py-3 text-right">
                                <div className="font-semibold text-orange-600">${stat.totalCut.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
                                {prevStat && <div className={`text-[10px] font-semibold ${cutChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>{cutChange >= 0 ? '↑' : '↓'} {Math.abs(cutChange).toFixed(1)}%</div>}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  )}
                </div>

                {/* Yearly Stats */}
                <div>
                  <button
                    onClick={() => {
                      setExpandedStatsSections(prev => {
                        const newSet = new Set(prev);
                        if (newSet.has('yearly')) {
                          newSet.delete('yearly');
                        } else {
                          newSet.add('yearly');
                        }
                        return newSet;
                      });
                    }}
                    className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-emerald-600" />
                      <h3 className="text-lg font-bold text-slate-900">
                        Yearly Performance (2026)
                      </h3>
                    </div>
                    {expandedStatsSections.has('yearly') ? (
                      <ChevronUp className="w-5 h-5 text-slate-600" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-slate-600" />
                    )}
                  </button>
                  {expandedStatsSections.has('yearly') && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100 border-b border-slate-200">
                        <tr>
                          <th className="px-4 py-3 text-left font-semibold text-slate-700">Year</th>
                          <th className="px-4 py-3 text-right font-semibold text-slate-700">Total Gross</th>
                          <th className="px-4 py-3 text-right font-semibold text-slate-700">Total Miles</th>
                          <th className="px-4 py-3 text-right font-semibold text-slate-700">Avg per Mile</th>
                          <th className="px-4 py-3 text-right font-semibold text-slate-700">Total Cut</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        {statsData.yearly.map((stat: any, idx: number) => {
                          const prevStat = idx > 0 ? statsData.yearly[idx - 1] : null;
                          const grossChange = prevStat && prevStat.totalGross > 0 ? ((stat.totalGross - prevStat.totalGross) / prevStat.totalGross) * 100 : 0;
                          const milesChange = prevStat && prevStat.totalMiles > 0 ? ((stat.totalMiles - prevStat.totalMiles) / prevStat.totalMiles) * 100 : 0;
                          const rpmChange = prevStat && prevStat.avgPerMile > 0 ? ((stat.avgPerMile - prevStat.avgPerMile) / prevStat.avgPerMile) * 100 : 0;
                          const cutChange = prevStat && prevStat.totalCut > 0 ? ((stat.totalCut - prevStat.totalCut) / prevStat.totalCut) * 100 : 0;
                          
                          return (
                            <tr key={idx} className="hover:bg-slate-50">
                              <td className="px-4 py-3 font-medium text-slate-900">{stat.period}</td>
                              <td className="px-4 py-3 text-right">
                                <div className="font-semibold text-green-600">${stat.totalGross.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
                                {prevStat && <div className={`text-[10px] font-semibold ${grossChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>{grossChange >= 0 ? '↑' : '↓'} {Math.abs(grossChange).toFixed(1)}%</div>}
                              </td>
                              <td className="px-4 py-3 text-right">
                                <div className="font-semibold text-blue-600">{stat.totalMiles.toLocaleString()}</div>
                                {prevStat && <div className={`text-[10px] font-semibold ${milesChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>{milesChange >= 0 ? '↑' : '↓'} {Math.abs(milesChange).toFixed(1)}%</div>}
                              </td>
                              <td className="px-4 py-3 text-right">
                                <div className="font-semibold text-purple-600">${stat.avgPerMile.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
                                {prevStat && <div className={`text-[10px] font-semibold ${rpmChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>{rpmChange >= 0 ? '↑' : '↓'} {Math.abs(rpmChange).toFixed(1)}%</div>}
                              </td>
                              <td className="px-4 py-3 text-right">
                                <div className="font-semibold text-orange-600">${stat.totalCut.toLocaleString('en-US', { minimumFractionDigits: 2 })}</div>
                                {prevStat && <div className={`text-[10px] font-semibold ${cutChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>{cutChange >= 0 ? '↑' : '↓'} {Math.abs(cutChange).toFixed(1)}%</div>}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  )}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
